import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PhotoUpload } from '../components/PhotoUpload';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, X, ArrowRight, ArrowLeft, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import { useUnsavedChangesWarning, confirmDiscard } from '../hooks/useUnsavedChangesWarning';
import { useCanvasSignature } from '../hooks/useCanvasSignature';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_TYPES = [
  'Polvo Químico Seco',
  'Dióxido de Carbono',
  'Acetato de Potasio',
  'Agua a Presión',
  'Agua Desionizada',
  'Sal Metálica con Aditivos',
  'Encapsulador de Micelas F500'
];

const STAGE1_FIELDS = [
  { key: 'manijas', label: 'Manijas' },
  { key: 'manguera_boquilla', label: 'Manguera / Boquilla' },
  { key: 'abrazadera', label: 'Abrazadera' },
  { key: 'manometro_visual', label: 'Manómetro' },
  { key: 'cola_de_rata', label: 'Cola de Rata' },
  { key: 'abolladuras', label: 'Abolladuras en Cilindro' },
  { key: 'seguro_galvanizado', label: 'Seguro Galvanizado' }
];

// Campos de resultado de inspección visual (Etapa 1)
const STAGE1_RESULT_FIELDS = [
  { key: 'requiere_recarga', label: 'Requiere Mtto y Recarga' },
  { key: 'requiere_prueba_hidrostatica', label: '¿Requiere Prueba Hidrostática?' }
];

export const NewOrderFlowPage = () => {
  const navigate = useNavigate();
  const {
    canvasRef,
    handlers: signatureHandlers,
    clear: clearSignature,
    isEmpty: isSignatureEmpty,
    toDataURL: signatureToDataURL,
  } = useCanvasSignature();
  
  // Step control
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Order data
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [orderData, setOrderData] = useState({
    reception_location: 'TIENDA SIGAL EXTINTORES',
    reception_date: new Date().toISOString().split('T')[0]
  });
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  
  // Step 2: Extinguishers
  const [extinguishers, setExtinguishers] = useState([]);
  const [currentExtinguisher, setCurrentExtinguisher] = useState({
    agent_type: 'Polvo Químico Seco',
    capacity_value: '',
    capacity_unit: 'KG',
    manufacture_date: '',
    last_recharge_date: '',
    has_hydrostatic_test: 'N/A',
    stage_1_data: {
      // Inspección Visual
      manijas: '',
      manguera_boquilla: '',
      abrazadera: '',
      manometro_visual: '',
      cola_de_rata: '',
      abolladuras: '',
      seguro_galvanizado: '',
      // Resultado de la Inspección Visual (Sí / No / N/A)
      requiere_recarga: '',
      requiere_prueba_hidrostatica: '',
      observaciones: ''
    },
    photos: []
  });
  
  // Step 3: Signature
  const [receptionData, setReceptionData] = useState({
    received_by_name: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const submissionInFlightRef = useRef(false);

  // Warn the user before closing/refreshing the tab if any order data has been entered.
  const hasUnsavedData =
    selectedClient !== null ||
    extinguishers.length > 0 ||
    receptionData.received_by_name.trim() !== '' ||
    (newClient.name || newClient.phone || newClient.email);
  useUnsavedChangesWarning(hasUnsavedData && !submitting);

  const safeCancel = () => {
    if (hasUnsavedData && !confirmDiscard('¿Cancelar la creación de la orden? Se perderá toda la información ingresada.')) return;
    navigate('/orders');
  };

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.phone) {
      toast.error('Nombre y teléfono son requeridos');
      return;
    }

    try {
      const response = await axios.post(`${API}/clients`, newClient);
      setClients([...clients, response.data]);
      setSelectedClient(response.data.id);
      setShowNewClientForm(false);
      setNewClient({ name: '', phone: '', email: '', address: '' });
      toast.success('Cliente creado exitosamente');
    } catch (error) {
      logger.error('Error creating client:', error);
      toast.error('Error al crear cliente');
    }
  };

  const handleStep1Next = () => {
    if (!selectedClient) {
      toast.error('Selecciona un cliente');
      return;
    }
    setCurrentStep(2);
  };

  const handleSaveExtinguisher = () => {
    if (!currentExtinguisher.capacity_value) {
      toast.error('La capacidad es requerida');
      return;
    }
    if (!currentExtinguisher.manufacture_date) {
      toast.error('La fecha de fabricación es requerida');
      return;
    }

    // Validar que todos los campos de la Etapa 1 estén completos
    const s1 = currentExtinguisher.stage_1_data;
    const requiredKeys = [
      ...STAGE1_FIELDS.map((f) => f.key),
      ...STAGE1_RESULT_FIELDS.map((f) => f.key),
    ];
    const missing = requiredKeys.filter((k) => !s1[k] || s1[k] === '');
    if (missing.length > 0) {
      toast.error('Completa todos los campos de la Etapa 1 antes de agregar el extintor', { duration: 5000 });
      return;
    }
    if (!s1.observaciones || !s1.observaciones.trim()) {
      toast.error('Observaciones es obligatorio en Etapa 1');
      return;
    }

    setExtinguishers([...extinguishers, { ...currentExtinguisher, _uid: crypto.randomUUID() }]);
    
    // Reset current extinguisher
    setCurrentExtinguisher({
      agent_type: 'Polvo Químico Seco',
      capacity_value: '',
      capacity_unit: 'KG',
      manufacture_date: '',
      last_recharge_date: '',
      has_hydrostatic_test: 'N/A',
      stage_1_data: {
        // Inspección Visual
        manijas: '',
        manguera_boquilla: '',
        abrazadera: '',
        manometro_visual: '',
        cola_de_rata: '',
        abolladuras: '',
        seguro_galvanizado: '',
        // Resultado de la Inspección Visual (Sí / No / N/A)
        requiere_recarga: '',
        requiere_prueba_hidrostatica: '',
        observaciones: ''
      },
      photos: []
    });

    toast.success(`Extintor ${extinguishers.length + 1} agregado correctamente`);
  };

  const handleFinishInspection = () => {
    if (extinguishers.length === 0) {
      toast.error('Debes agregar al menos un extintor');
      return;
    }
    setCurrentStep(3);
  };

  const handleFinalSubmit = async () => {
    if (!receptionData.received_by_name) {
      toast.error('El nombre de quien entrega es requerido');
      return;
    }
    if (isSignatureEmpty()) {
      toast.error('La firma es requerida');
      return;
    }

    // Hard guard against double-submit (rapid clicks / offline network delay).
    // The setSubmitting flag is async — a ref-based lock catches concurrent calls
    // before React re-renders.
    if (submissionInFlightRef.current) return;
    submissionInFlightRef.current = true;

    const signatureData = signatureToDataURL('image/png');

    setSubmitting(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        client_id: selectedClient,
        reception_date: new Date(orderData.reception_date).toISOString(),
        reception_location: orderData.reception_location,
        reception_received_by_name: receptionData.received_by_name,
        reception_signature: signatureData
      });

      const orderId = orderResponse.data.id;

      // Create extinguishers with Stage 1 data
      for (const ext of extinguishers) {
        const extResponse = await axios.post(`${API}/extinguishers`, {
          order_id: orderId,
          agent_type: ext.agent_type,
          capacity_value: ext.capacity_value,
          capacity_unit: ext.capacity_unit,
          manufacture_date: ext.manufacture_date || null,
          last_recharge_date: ext.last_recharge_date || null,
          has_hydrostatic_test: ext.has_hydrostatic_test
        });

        // Update extinguisher with Stage 1 data
        await axios.put(`${API}/extinguishers/${extResponse.data.id}/stage/1`, {
          stage_data: ext.stage_1_data,
          photos: ext.photos
        });
      }

      toast.success('Orden creada exitosamente');
      navigate(`/orders/${orderId}`);
    } catch (error) {
      logger.error('Error creating order:', error);
      toast.error('Error al crear la orden');
    } finally {
      setSubmitting(false);
      submissionInFlightRef.current = false;
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Progress indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`flex items-center justify-center w-10 h-10 border-2 ${currentStep >= 1 ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
              {currentStep > 1 ? <Check size={20} weight="bold" /> : '1'}
            </div>
            <div className={`h-1 w-24 ${currentStep >= 2 ? 'bg-primary' : 'bg-border'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 border-2 ${currentStep >= 2 ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
              {currentStep > 2 ? <Check size={20} weight="bold" /> : '2'}
            </div>
            <div className={`h-1 w-24 ${currentStep >= 3 ? 'bg-primary' : 'bg-border'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 border-2 ${currentStep >= 3 ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
              3
            </div>
          </div>
          <Button
            variant="outline"
            onClick={safeCancel}
            className="hover:bg-secondary"
          >
            Cancelar
          </Button>
        </div>

        {/* Step 1: Order Data */}
        {currentStep === 1 && (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Paso 1: Datos de la Orden</h1>
              <p className="text-base text-muted-foreground mt-2">Información básica de la recepción</p>
            </div>

            <div className="p-8 border border-border bg-background space-y-6">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Cliente</h2>
              
              {!showNewClientForm ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Cliente *</Label>
                    <select
                      value={selectedClient || ''}
                      onChange={(e) => setSelectedClient(e.target.value)}
                      className="w-full px-4 py-2 border border-border bg-background text-foreground"
                      required
                    >
                      <option value="">Seleccionar cliente...</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name} - {client.phone}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewClientForm(true)}
                    className="hover:bg-primary hover:text-primary-foreground"
                  >
                    <Plus size={16} className="mr-2" />
                    Crear Nuevo Cliente
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 p-6 bg-secondary">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Nuevo Cliente</h3>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewClientForm(false)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Nombre *</Label>
                    <Input
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Teléfono *</Label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Email</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      placeholder="ejemplo@correo.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Dirección</Label>
                    <Input
                      value={newClient.address}
                      onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleCreateClient}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Guardar Cliente
                  </Button>
                </div>
              )}
            </div>

            <div className="p-8 border border-border bg-background space-y-6">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Detalles de Recepción</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Lugar de Recepción *</Label>
                  <select
                    value={orderData.reception_location}
                    onChange={(e) => setOrderData({ ...orderData, reception_location: e.target.value })}
                    className="w-full px-4 py-2 border border-border bg-background text-foreground"
                    required
                  >
                    <option value="TIENDA SIGAL EXTINTORES">TIENDA SIGAL EXTINTORES</option>
                    <option value="DOMICILIO DE CLIENTE">DOMICILIO DE CLIENTE</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Fecha de Recepción *</Label>
                  <Input
                    type="date"
                    value={orderData.reception_date}
                    onChange={(e) => setOrderData({ ...orderData, reception_date: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleStep1Next}
                className="bg-primary hover:bg-primary/90"
                data-testid="step1-next-button"
              >
                Comenzar Inspección
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Extinguisher Inspection */}
        {currentStep === 2 && (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
                Paso 2: Inspección de Extintor #{extinguishers.length + 1}
              </h1>
              <p className="text-base text-muted-foreground mt-2">
                Completa los datos e inspección visual
              </p>
              {extinguishers.length > 0 && (
                <p className="text-sm text-primary font-semibold mt-1">
                  ✓ {extinguishers.length} extintor(es) ya inspeccionado(s)
                </p>
              )}
            </div>

            {/* Extinguisher Data */}
            <div className="p-8 border border-border bg-background space-y-6">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Datos del Extintor</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Tipo de Agente *</Label>
                  <select
                    value={currentExtinguisher.agent_type}
                    onChange={(e) => setCurrentExtinguisher({ ...currentExtinguisher, agent_type: e.target.value })}
                    className="w-full px-4 py-2 border border-border bg-background text-foreground"
                    required
                  >
                    {AGENT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Capacidad *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      step="0.5"
                      value={currentExtinguisher.capacity_value}
                      onChange={(e) => setCurrentExtinguisher({ ...currentExtinguisher, capacity_value: e.target.value })}
                      placeholder="Ej: 6, 9, 12"
                      required
                    />
                    <select
                      value={currentExtinguisher.capacity_unit}
                      onChange={(e) => setCurrentExtinguisher({ ...currentExtinguisher, capacity_unit: e.target.value })}
                      className="w-full px-4 py-2 border border-border bg-background text-foreground"
                      required
                    >
                      <option value="KG">KG</option>
                      <option value="LBS">LBS</option>
                      <option value="LT">LT</option>
                    </select>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Unidad de Medida: KG (Kilogramos), LBS (Libras), LT (Litros)</span>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Fecha de Fabricación</Label>
                  <Input
                    type="date"
                    value={currentExtinguisher.manufacture_date}
                    onChange={(e) => setCurrentExtinguisher({ ...currentExtinguisher, manufacture_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Última Recarga</Label>
                  <Input
                    type="date"
                    value={currentExtinguisher.last_recharge_date}
                    onChange={(e) => setCurrentExtinguisher({ ...currentExtinguisher, last_recharge_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">¿Cuenta con Prueba Hidrostática?</Label>
                  <select
                    value={currentExtinguisher.has_hydrostatic_test}
                    onChange={(e) => setCurrentExtinguisher({ ...currentExtinguisher, has_hydrostatic_test: e.target.value })}
                    className="w-full px-4 py-2 border border-border bg-background text-foreground"
                  >
                    <option value="SI">SI</option>
                    <option value="NO">NO</option>
                    <option value="N/A">N/A</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Stage 1 Inspection */}
            <div className="p-8 border border-border bg-background space-y-6">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Etapa 1: Inspección Visual</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {STAGE1_FIELDS.map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                      {label} <span className="text-destructive">*</span>
                    </Label>
                    <select
                      value={currentExtinguisher.stage_1_data[key] || ''}
                      onChange={(e) => setCurrentExtinguisher({
                        ...currentExtinguisher,
                        stage_1_data: {
                          ...currentExtinguisher.stage_1_data,
                          [key]: e.target.value
                        }
                      })}
                      className="w-full px-4 py-2 border border-border bg-background text-foreground"
                    >
                      <option value="">— Selecciona —</option>
                      <option value="Tiene">Tiene</option>
                      <option value="No tiene">No tiene</option>
                      <option value="No aplica">No aplica</option>
                    </select>
                  </div>
                ))}
              </div>

              {/* Resultado de la Inspección Visual */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h4 className="text-base font-semibold">Resultado de la Inspección Visual</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {STAGE1_RESULT_FIELDS.map(({ key, label }) => {
                    const raw = currentExtinguisher.stage_1_data[key];
                    const displayValue = raw === true ? 'Sí' : raw === false ? 'No' : (raw || '');
                    return (
                      <div key={key} className="space-y-2">
                        <Label htmlFor={`res-${key}`} className="text-xs font-bold uppercase tracking-[0.2em]">
                          {label} <span className="text-destructive">*</span>
                        </Label>
                        <select
                          id={`res-${key}`}
                          value={displayValue}
                          onChange={(e) => setCurrentExtinguisher({
                            ...currentExtinguisher,
                            stage_1_data: {
                              ...currentExtinguisher.stage_1_data,
                              [key]: e.target.value
                            }
                          })}
                          data-testid={`select-${key}`}
                          className="w-full px-4 py-2 border border-border bg-background text-foreground"
                        >
                          <option value="">— Selecciona —</option>
                          <option value="Sí">Sí</option>
                          <option value="No">No</option>
                          <option value="N/A">No aplica</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Observaciones Etapa 1 */}
              <div className="space-y-2 pt-4 border-t border-border">
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                  Observaciones <span className="text-destructive">*</span>
                </Label>
                <textarea
                  value={currentExtinguisher.stage_1_data.observaciones || ''}
                  onChange={(e) => setCurrentExtinguisher({
                    ...currentExtinguisher,
                    stage_1_data: {
                      ...currentExtinguisher.stage_1_data,
                      observaciones: e.target.value
                    }
                  })}
                  placeholder="Observaciones adicionales..."
                  className="w-full px-4 py-2 border border-border bg-background text-foreground min-h-[100px]"
                  data-testid="observaciones-input"
                />
              </div>
            </div>

            {/* Photos */}
            <div className="p-8 border border-border bg-background">
              <PhotoUpload
                photos={currentExtinguisher.photos}
                onPhotosChange={(photos) => setCurrentExtinguisher({ ...currentExtinguisher, photos })}
                maxPhotos={10}
              />
            </div>

            {/* Action buttons */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(1)}
                className="hover:bg-secondary"
              >
                <ArrowLeft size={20} className="mr-2" />
                Volver
              </Button>

              <div className="flex gap-3">
                <Button
                  onClick={handleSaveExtinguisher}
                  variant="outline"
                  className="hover:bg-primary hover:text-primary-foreground"
                  data-testid="save-and-add-button"
                >
                  <Plus size={20} className="mr-2" />
                  Guardar y Agregar Otro
                </Button>
                <Button
                  onClick={handleFinishInspection}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="finish-inspection-button"
                >
                  Finalizar Inspección
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Signature */}
        {currentStep === 3 && (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Paso 3: Firma de Aceptación</h1>
              <p className="text-base text-muted-foreground mt-2">Resumen y firma del cliente</p>
            </div>

            {/* Summary */}
            <div className="p-8 border border-border bg-background space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                Resumen: {extinguishers.length} Extintor(es) Inspeccionado(s)
              </h2>
              
              <div className="space-y-3">
                {extinguishers.map((ext, index) => (
                  <div key={ext._uid} className="p-4 bg-secondary border border-border">
                    <p className="font-bold">Extintor #{index + 1}</p>
                    <p className="text-sm text-muted-foreground">
                      {ext.agent_type} - {ext.capacity_value} {ext.capacity_unit}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {ext.photos.length} foto(s) · Etapa 1 completada
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Signature form */}
            <div className="p-8 border border-border bg-background space-y-6">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Datos de Entrega</h2>
              
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                  Nombre de quien Entrega los Extintores *
                </Label>
                <Input
                  value={receptionData.received_by_name}
                  onChange={(e) => setReceptionData({ ...receptionData, received_by_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                  Firma de Aceptación *
                </Label>
                <div className="border-2 border-border bg-white">
                  <canvas
                    ref={canvasRef}
                    width={700}
                    height={200}
                    {...signatureHandlers}
                    className="w-full cursor-crosshair touch-none select-none"
                    data-testid="signature-canvas"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearSignature}
                  className="hover:bg-secondary"
                >
                  Limpiar Firma
                </Button>
              </div>
            </div>

            {/* Final actions */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(2)}
                className="hover:bg-secondary"
              >
                <ArrowLeft size={20} className="mr-2" />
                Volver
              </Button>

              <Button
                onClick={handleFinalSubmit}
                disabled={submitting}
                className="bg-primary hover:bg-primary/90"
                data-testid="complete-order-button"
              >
                {submitting ? 'Procesando...' : 'Completar Recepción'}
                <Check size={20} className="ml-2" weight="bold" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
